/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {RefObject} from 'react';
import {error, info} from '@ion/core/src/logger/logger';
import {LogicalPosition} from '../types/editors';

interface WaitForElementParams {
  id: string,
  callback: (el: HTMLElement | null) => void,
  interval?: number;
  maxAttempts?: number
};

/**
 * Help to find, is application running in Safari Browser.
 *
 * ```typescript
 *
 *   import {isSafari} from '@ion/core/src/utils/BrowserUtils';
 *
 *   isSafari(); // true / false
 *
 * ```
 *
 */
export const isSafari = () => {
  const userAgent = navigator?.userAgent ?? '';
  return userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Chromium');
};

/**
 * Help to find, is application running in Firefox Browser.
 *
 * ```typescript
 *
 *   import {isFirefox} from '@ion/core/src/utils/BrowserUtils';
 *
 *   isFirefox(); // true / false
 *
 * ```
 *
 */
export const isFirefox = () => {
  const userAgent = navigator?.userAgent ?? '';
  return userAgent.includes('Firefox');
};

/**
 * The method helps scroll the page or container from the top of the page.
 *
 * ```typescript
 *
 *  import {smoothScroll} from '@ion/core/src/utils/BrowserUtils';
 *
 *  smoothScroll(250, null); // scroll the page from top to bottom
 *  smoothScroll(250, document.getElementById('id')); // scroll the container from top
 *  smoothScroll(250, null, 2); // scroll the page from top to bottom by 2 stpes.
 *
 * ```
 *
 * @param top page offset position to top
 * @param element HTML element if that need to scroll.
 * @param steps this indicates the step that must involved to scroll and this is
 * indirectly proportional to the speed of the scrolling.
 *
 */
export const smoothScroll = (top: number, element: HTMLElement | null, steps = 10) => {
  let scrollTop = 0;
  const scroll = () => {
    if (element) {
      element.scrollTo(0, scrollTop);
    } else {
      window.scrollBy(0, scrollTop);
    }
    if (scrollTop < top) {
      scrollTop += (top / steps);
      window.requestAnimationFrame(scroll);
    }
  };
  scroll();
};

/**
 * The method just scroll the window/container to make sure the
 * viewing of the html element.
 *
 * ```typescript
 *
 *  import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
 *
 *  smoothScrollByElement('elementID');
 *
 * ```
 *
 * @param elementID id of the html element.
 */
export const smoothScrollByElement = (
  elementID: string,
  block: ScrollLogicalPosition | LogicalPosition = LogicalPosition.Start
) => {
  const element = document.getElementById(elementID);
  element?.scrollIntoView({
    behavior : 'smooth',
    block
  });
};

/**
 * The method just scroll the window/container to make sure the
 * viewing of the html element.
 *
 * ```typescript
 *
 *  import {smoothScrollByRef} from '@ion/core/src/utils/BrowserUtils';
 *
 *  const myRef = React.useRef<HTMLDivElement>(null);
 *  smoothScrollByRef(myRef);
 *
 * ```
 *
 * @param ref is an object of type React.RefObject<HTMLElement>. It point
 * to the actual DOM element (like a `<div ref={myRef}>`)
 */
export const smoothScrollByRef = (
  ref: RefObject<HTMLElement>,
  block: ScrollLogicalPosition | LogicalPosition = LogicalPosition.Start
) => {
  ref?.current?.scrollIntoView({
    behavior : 'smooth',
    block
  });
};

/**
 * The method allow the client to delete the all of particular html element's
 * immediate children by its element ID. This method returns a promise, so
 * clients can trackwait for the dom update state and proceed the further steps.
 *
 * ```typescript
 *
 *  import {removeAllChildren} from '@ion/core/src/utils/BrowserUtils';
 *
 *  removeAllChildren('elementID');
 *
 * ```
 */
export const removeAllChildren = (elementID: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const node= document.getElementById(elementID);
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }
    resolve(true);
  });
};

/**
 * Hides the element with the given id, leaving it in the page. Meant for
 * an element React renders, such as a tooltip's portal: React still owns
 * the node and removes it itself when it unmounts, where taking it out of
 * the page by hand leaves React removing a node already gone.
 *
 * ```typescript
 *
 *  import {hideElementById} from '@ion/core/src/utils/BrowserUtils';
 *  ...
 *
 *  // the tooltip is hidden at once, and removed by React later ...
 *  hideElementById('findNearbyTooltip');
 *
 * ```
 *
 * @param id The id of the element to hide.
 */
export const hideElementById = (id: string): void => {
  const element = document.getElementById(id);
  if (element) {
    element.style.visibility = 'hidden';
  }
};

/**
 * The method helps string that will be copied in to clipboard
 *
 * ```typescript
 * import {copyToClipBoard} from '@ion/core/src/utils/BrowserUtils';
 *
 * @param value
 * @returns // true / false
 */

export const copyToClipBoard = (value: string): boolean => {
  try {
    const {clipboard} = navigator;
    if (clipboard) {
      clipboard.writeText(value);
      return true;
    }
  } catch (err) {
    error(`Error writting to User Clipboard: ${err.toString()}`);
  }
  return false;
};

/**
 * The changeURLPath utility is used to change the URL path of the browser window
 * as per clients need.
 *
 * ```typescript
 *
 *  import {changeURLPath} from '@ion/core/src/utils/';
 *
 *  changeURLPath('reports#report');
 *
 * ```
 */
export const changeURLPath = (value: string, window = globalThis) => {
  const timeout = setTimeout(() => {
    window.history.pushState({}, document.title, value);
    info(`Route changed to ${value}`);
    clearTimeout(timeout);
  }, 350);
};

/**
 * Polls the DOM at regular intervals to wait for an element with
 * the given ID to appear. If the element is found within the
 * maxAttempts, it calls the provided callback with the element.
 * If not found after maxAttempts, the callback is called with `null`.
 * Pass `maxAttempts: Infinity` to wait until the element appears, and
 * call the returned function to stop waiting (e.g. on unmount).
 *
 * ```typescript
 * 
 * import {waitForElement} from '@ion/core/src/utils/BrowserUtils';
 * 
 * waitForElement({
 *   id: 'my-element',
 *   callback: (el) => {
 *     if (el) {
 *       console.log('Element found:', el);
 *     } else {
 *       console.warn('Element not found after max attempts');
 *     }
 *   },
 *   interval: 200,
 *   maxAttempts: 30
 * });
 * 
 * ```
 *
 * @param {WaitForElementParams} params - Configuration options for the wait logic
 * @returns Stops waiting - the callback is then never called.
 */
export const waitForElement = ({
  id,
  callback,
  interval = 100,
  maxAttempts = 50
}: WaitForElementParams): (() => void) => {
  let attempts = 0;
  const timer = setInterval(() => {
    const el = document.getElementById(id);
    attempts++;
    if (el || attempts > maxAttempts) {
      clearInterval(timer);
      callback(el);
    }
  }, interval);

  return function() {
    clearInterval(timer);
  };
};
